/**
 * Consignes par type d'exercice pour l'export PDF des évaluations.
 *
 * Source de vérité centralisée — évite que chaque renderer print ait sa propre
 * formulation. Pour modifier une tournure, ne modifier qu'ici.
 */

export const CONSIGNES = {
    qcm_simple: 'Entoure la bonne réponse.',
    qcm_multi: 'Entoure la ou les bonnes réponses.',
    vf: 'Coche la case correspondante pour chaque affirmation.',
    table: 'Coche la bonne réponse dans le tableau.',
    relier: 'Relie chaque élément de la colonne de gauche à l\'élément correspondant à droite.',
    relier_leurres: 'Relie chaque élément de la colonne de gauche à l\'élément correspondant à droite. Attention : certaines étiquettes de droite ne servent pas.',
    ordonner: 'Numérote les propositions dans le bon ordre.',
    classer: 'Recopie chaque élément dans la colonne correspondante.',
    texte_trou_drag: 'Complète les espaces en choisissant le bon mot dans la liste.',
    texte_trou_saisie: 'Complète les espaces avec les bonnes valeurs.',
    saisie: 'Réponds dans l\'espace prévu.',
    placer_points: 'Place les points demandés sur la droite graduée.',
    placer_points_2d: 'Place les points demandés dans le repère.',
    programme_construction: 'Réalise la construction demandée dans l\'espace de travail ci-dessous.',
    placer_aiguilles: 'Dessine les aiguilles sur le cadran pour indiquer l\'heure demandée.',
};

/**
 * Sélectionne la consigne adaptée selon le payload.
 * @param {Object} payload - payload TanoEd (type / contenu)
 * @returns {string} consigne en français
 */
/**
 * Clé de consigne pour un payload — partagée entre les consignes « feuille »
 * (CONSIGNES) et « cahier » (CONSIGNES_CAHIER de saisiePolicy).
 * @returns {string|null} une clé de CONSIGNES, ou null si type inconnu
 */
export const consigneKeyFromPayload = (payload) => {
    if (!payload) return null;
    const type = payload.type;
    const subType = payload.contenu?.subType;
    const multi = payload.contenu?.multi;

    // Exercices à tableau : type 'qcm' mais la réponse se donne DANS le tableau
    // (elements_additionnels type 'table') — à tester AVANT le return qcm
    // générique, sinon "Entoure…" s'applique à tort (il n'y a rien à entourer).
    if (type === 'qcm') {
        const tableEl = payload.elements_additionnels?.elements?.find((e) => e.type === 'table');
        if (tableEl) {
            return tableEl.config?.type === 'vraiFaux' ? 'vf' : 'table';
        }
        return multi ? 'qcm_multi' : 'qcm_simple';
    }
    if (type === 'texte') return 'saisie';
    if (type === 'association') {
        if (subType === 'relier') {
            return payload.contenu?.cibles_leurres?.length ? 'relier_leurres' : 'relier';
        }
        if (subType === 'ordonner') return 'ordonner';
        if (subType === 'classer') return 'classer';
        if (subType === 'texte_trou') {
            return payload.contenu.mode === 'saisie' ? 'texte_trou_saisie' : 'texte_trou_drag';
        }
        if (subType === 'placer_points') return 'placer_points';
        if (subType === 'placer_points_2d') return 'placer_points_2d';
        if (subType === 'programme_construction') return 'programme_construction';
        if (subType === 'placer_aiguilles') return 'placer_aiguilles';
    }
    return null;
};

export const consigneFromPayload = (payload) => {
    const key = consigneKeyFromPayload(payload);
    return key ? CONSIGNES[key] || '' : '';
};

const MOIS_FR = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

/**
 * Formate une date ISO (YYYY-MM-DD) au format français "le 23 Avril 2026".
 * Fallback : renvoie la chaîne brute si parsing invalide.
 * On utilise 'T00:00:00' pour éviter les décalages de timezone (sinon une
 * date ISO seule est interprétée en UTC → jour parfois reculé d'un cran).
 */
export const formatDateFr = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return iso;
    return `le ${d.getDate()} ${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Sanitize un string pour `document.title` (nom de fichier PDF par défaut).
 * Remplace espaces + accents par _, retire caractères spéciaux.
 * Retourne '' si input vide/null (au caller de filtrer avec `.filter(Boolean)`).
 */
export const sanitizeTitleForPdf = (s) => {
    if (!s) return '';
    return s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')  // retire accents
        .replace(/[^\w\s-]/g, '')          // garde alphanum + espace + tiret
        .trim()
        .replace(/\s+/g, '_');
};
